import React, { useEffect, useState } from "react";
import {
Box,
Card,
Typography,
TextField,
Button,
Stack,
Checkbox,
FormControlLabel,
Snackbar,
Alert
} from "@mui/material";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import {
  adminPageSx,
  contentCardSx,
  mobileActionGroupSx
} from "../utils/adminResponsive";

const initialForm = {
  question: "",
  answer: "",
  sort_order: 0,
  is_active: true
};

export default function FAQs() {
  const [faqs, setFaqs] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const loadFaqs = async () => {
    setLoading(true);

    try {
      const data = await adminFetch("/api/admin/faqs", {
        method: "GET"
      });

      if (data?.status) {
        setFaqs(data.faqs || []);
      }
    } catch (error) {
      console.log(error);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadFaqs();
  }, []);

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    setSaving(true);

    try {
      const endpoint = editingId ? `/api/admin/faqs/${editingId}` : "/api/admin/faqs";
      const method = editingId ? "PUT" : "POST";

      const data = await adminFetch(endpoint, {
        method,
        body: JSON.stringify(formData)
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to save FAQ", "error");
        setSaving(false);
        return;
      }

      showSnackbar(editingId ? "FAQ updated successfully" : "FAQ created successfully");
      resetForm();
      await loadFaqs();
    } catch (error) {
      console.log(error);
      showSnackbar("Server error", "error");
    }

    setSaving(false);
  };

  const faqColumns = [
    {
      key: "question",
      label: "Question",
      render: (faq) => faq.question
    },
    {
      key: "sort_order",
      label: "Sort Order",
      render: (faq) => faq.sort_order
    },
    {
      key: "status",
      label: "Status",
      render: (faq) => (faq.is_active ? "Active" : "Hidden")
    },
    {
      key: "actions",
      label: "Action",
      isActions: true,
      mobileValueSx: mobileActionGroupSx,
      render: (faq) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            setEditingId(faq.id);
            setFormData({
              question: faq.question || "",
              answer: faq.answer || "",
              sort_order: faq.sort_order || 0,
              is_active: Boolean(faq.is_active)
            });
          }}
        >
          Edit
        </Button>
      )
    }
  ];

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
        FAQ Management
      </Typography>

      <Card sx={{ p: { xs: 2, sm: 4 }, mb: 3, ...contentCardSx }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
          {editingId ? "Update FAQ" : "Create FAQ"}
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Question"
            value={formData.question}
            onChange={(e) => updateField("question", e.target.value)}
            fullWidth
          />
          <TextField
            label="Answer"
            value={formData.answer}
            onChange={(e) => updateField("answer", e.target.value)}
            fullWidth
            multiline
            rows={5}
          />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Sort Order"
              type="number"
              value={formData.sort_order}
              onChange={(e) => updateField("sort_order", e.target.value)}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.is_active)}
                  onChange={(e) => updateField("is_active", e.target.checked)}
                />
              }
              label="Active on landing page"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              sx={{ bgcolor: Colors.accent, color: "#003300", fontWeight: 700 }}
              onClick={handleSubmit}
              disabled={saving}
            >
              {editingId ? "Update FAQ" : "Create FAQ"}
            </Button>
            {editingId && (
              <Button variant="outlined" onClick={resetForm}>
                Cancel Edit
              </Button>
            )}
          </Stack>
        </Stack>
      </Card>

      <Card sx={{ p: 2, ...contentCardSx }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Existing FAQs
        </Typography>

        <Box sx={{ position: "relative", minHeight: 320 }}>
          <ResponsiveDataTable
            columns={faqColumns}
            rows={faqs}
            loading={loading}
            minWidth={720}
            emptyMinHeight={180}
            emptyMessage="No FAQs created yet"
            mobileTitle={(faq) => faq.question}
          />

          <TableLoadingOverlay loading={loading} />
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
